import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { MenuItem, Menu, useMenu } from 'hooks';
import { Fragment, useEffect, useState } from 'react';
import * as i18n from 'i18next';
import { animated, useSpring } from 'react-spring';

require('./MainMenu.css');

function deepCountItems(menus: Menu[], currentIndex: number, pathname: string): number {
    if (currentIndex >= menus.length) {
        return 0;
    }

    const menu = menus[currentIndex];

    let descendentItemCount = 0;
    for (const item of menu.items) {
        const exactMatch = pathname === item.link;
        const partialMatch = pathname.indexOf(item.link) === 0 && !exactMatch;
        const subMenuExists = currentIndex < menus.length - 1;
        if (subMenuExists && partialMatch) {
            descendentItemCount = deepCountItems(menus, currentIndex + 1, pathname);
        }
    }

    const itemCount = menus[currentIndex].items.length;
    return itemCount + descendentItemCount;
}

const MenuPanel = ({ menu, visible, currentIndex = 0 }: { menu: Menu | null; visible: boolean; currentIndex?: number }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { menus } = useMenu();
    const [ internalMenu, setInternalMenu ] = useState(menu);

    const emHeight = 3.5;

    const deepItemCount = visible ? deepCountItems(menus, currentIndex, pathname) : 0;

    const [ style, styleAnimApi ] = useSpring(() => ({
        config: { mass: 1, tension: 400, friction: 30 },
        height: visible && menu ? `${deepItemCount * emHeight}em` : '0em',
        opacity: visible ? 1 : 0,
    }));

    // Store a copy of the menu when it gets set. Having our own
    // copy allows us to animate it out gracefully after it has
    // been set to null.
    useEffect(() => {
        let mounted = true;
        if (menu && visible) {
            setInternalMenu(menu);
            styleAnimApi.start({
                height: `${deepItemCount * emHeight}em`,
                opacity: 1,
            });
        }
        else {
            styleAnimApi.start({
                height: '0em',
                opacity: 0,
                onResolve: () => {
                    // Get rid of the menu once the exit animation has completed
                    if (mounted) {
                        setInternalMenu(menu);
                    }
                },
            });
        }
        return () => {
            mounted = false;
            styleAnimApi.stop();
        };
    }, [ menu, visible, deepItemCount, styleAnimApi, setInternalMenu ]);

    // Stop animations when we get unmounted.
    useEffect(() => {
        return () => {
            styleAnimApi.stop();
        };
    }, [ styleAnimApi ]);

    if (!internalMenu) {
        return null;
    }
    return (
        <animated.nav
            id={ internalMenu.id }
            style={ style }
            key={ `nav-${internalMenu.id}` }
        >
            { internalMenu.items.filter(item => !!item.id).map(item =>
                renderMenuItem(item, menus, currentIndex, pathname, t)
            ) }
        </animated.nav>
    );
};

function renderMenuItem(item: MenuItem, menus: Menu[], currentIndex: number, pathname: string, t: i18n.TFunction) {
    const exactMatch = pathname === item.link;
    const partialMatch = pathname.indexOf(item.link) === 0 && !exactMatch;
    const subMenuExists = currentIndex < menus.length - 1;
    const subMenu = subMenuExists && partialMatch ? menus[currentIndex + 1] : null;
    return (
        <Fragment key={ `${item.id}-fragment` }>
            <Link
                id={ item.id }
                to={ item.link }
                className={ exactMatch ? 'active' : '' }
            >
                <span>
                    { item.text || t(item.textKey!) }
                </span>
            </Link>
            <MenuPanel
                visible={ partialMatch && subMenuExists }
                menu={ subMenu }
                currentIndex={ currentIndex + 1 }
            />
        </Fragment>
    );
}


export const MainMenu = () => {
    const { menus } = useMenu();
    return (
        <nav
            id="main-menu"
        >
            <MenuPanel
                visible={ true }
                menu={ menus[0] }
            />
        </nav>
    );
};
